import { useState, useEffect } from "react";
import { Paper, Box, Typography, Tooltip, Link, Checkbox } from "@mui/material";
import { DataGrid, GridColDef, GridCellParams, MuiEvent } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { getCurrentUser } from "aws-amplify/auth";
import { useQueryClient } from "@tanstack/react-query";

import { formatDate } from "../../utils/timestamp";
import ExportAlerts from "./ExportAlerts";
import { dismissAlert } from "../../services/apis";
export interface AlertsTableRow {
  id: string;
  first_seen: string;
  last_seen: string;
  alertDescription: string;
  projectName: string;
  projectId: string;
  siteName: string;
  siteId: string;
  unit_name: string;
  unit: string;
  unitId: string;
  alert_dismissed: boolean;
}

export default function AlertsTable({ exportAlertsData, isLoadingAlerts, isSuccessAlerts, alertsRequestData, setAlertsRequestData, setEnableSearchQuery }: any) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isInitialRender, setInitialRender] = useState(true);
  const [totalRows, setTotalRows] = useState(10);
  const [sortModel, setSortModel] = useState(() => {
    const savedSortModel = localStorage.getItem("alertSortModel");
    return savedSortModel ? JSON.parse(savedSortModel) : [{ field: "alert_dismissed", sort: "asc" }];
  });
  const [alertsFilteredRows, setAlertsFilteredRows] = useState<AlertsTableRow[]>([]);
  const [paginationModel, setPaginationModel] = useState<any>(() => {
    const savedPaginationModel = localStorage.getItem("alertPaginationModel");
    return savedPaginationModel
      ? JSON.parse(savedPaginationModel)
      : {
          page: 0,
          pageSize: 10,
        };
  });
  const [userId, setUserId] = useState<string | null>(null);
  const [loadingDismissAlert, setLoadingDismissAlert] = useState(false);

  useEffect(() => {
    const fetchUserId = async () => {
      const user = await getCurrentUser();
      setUserId(user.userId);
    };
    fetchUserId();
  }, []);

  useEffect(() => {
    localStorage.setItem("alertPaginationModel", JSON.stringify(paginationModel));
  }, [paginationModel]);

  useEffect(() => {
    if (isInitialRender) {
      setInitialRender(false);
    }
  }, [isInitialRender]);

  const handleSortModelChange = (e: any) => {
    if (e?.length <= 0) return;
    setSortModel(e);
    setAlertsRequestData({ ...alertsRequestData, sortOrder: e[0]?.sort ?? "", sortField: e[0]?.field ?? "" });
    localStorage.setItem("alertSortModel", JSON.stringify(e)); // Save the sort model
  };

  useEffect(() => {
    if (exportAlertsData !== null && exportAlertsData !== undefined) {
      const updatedData: AlertsTableRow[] = [];
      for (const i in exportAlertsData?.active_alerts) {
        const itemSub = exportAlertsData?.active_alerts[i];
        // console.log("itemSub", itemSub);
        const { alert_id, first_seen, last_seen, message, project_name, project_id, site_name, site_id, unit_name, unit_id, alert_dismissed, unit } = itemSub;
        const sortedItem: AlertsTableRow = {
          id: alert_id,
          first_seen: formatDate(first_seen) || "--",
          last_seen: formatDate(last_seen) || "--",
          alertDescription: message,
          projectName: project_name,
          projectId: project_id,
          siteName: site_name,
          siteId: site_id,
          unit_name: unit_name,
          unit: unit,
          unitId: unit_id,
          alert_dismissed: alert_dismissed,
        };
        updatedData.push(sortedItem);
      }
      setTotalRows(exportAlertsData?.total_count);
      setAlertsFilteredRows(updatedData);
    }
  }, [exportAlertsData]);

  const columns: GridColDef<AlertsTableRow>[] = [
    {
      field: "alert_dismissed",
      headerName: "Acknowledged",
      minWidth: 130,
      renderCell: (params: any) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            borderRight: "1px solid #E2E9F4",
          }}
        >
          <Checkbox disabled={params.row.alert_dismissed} checked={params.row.alert_dismissed} />
        </Box>
      ),
    },
    { field: "first_seen", headerName: "Time Started", minWidth: 160 },
    { field: "last_seen", headerName: "Time Ended", minWidth: 160 },
    {
      field: "alertDescription",
      headerName: "Description",
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.value} arrow>
          <div
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "100%",
              cursor: "pointer",
            }}
          >
            {params.value}
          </div>
        </Tooltip>
      ),
    },
    {
      field: "unit",
      headerName: "Unit",
      flex: 1,
      renderCell: (params: any) => {
        return (
          <Tooltip title={params.value} placement="bottom-start">
            <Link
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#193561",
                cursor: "pointer",
                fontSize: "16px",
                whiteSpace: "pre",
              }}
            >
              {params.value}
            </Link>
          </Tooltip>
        );
      },
    },
  ];

  const handleCellClick = (params: GridCellParams, event: MuiEvent<React.MouseEvent<HTMLElement>>) => {
    event.defaultMuiPrevented = true; // prevents default MUI handling if needed
    // console.log("Cell clicked:", params);
    if (params.field === "projectName") {
      navigate(`/project-directory/project-dashboard/${encodeURIComponent(params.row.projectName)}/${params.row.projectId}`);
    } else if (params.field === "siteName") {
      navigate(
        `/project-directory/project-dashboard/site-dashboard/${encodeURIComponent(params.row.projectName)}/${params.row.projectId}/${encodeURIComponent(params.row.siteName)}/${params.row.siteId}`
      );
    } else if (params.field === "unit") {
      navigate(
        `/project-directory/project-dashboard/site-dashboard/unit-dashboard/${encodeURIComponent(params.row.projectName)}/${params.row.projectId}/${encodeURIComponent(params.row.siteName)}/${
          params.row.siteId
        }/${encodeURIComponent(params.row.unit)}/${params.row.unitId}`
      );
    } else if (params.field === "alert_dismissed") {
      // Handle checkbox click if needed
      if (!params.row.alert_dismissed) {
        const handleDismissAlert = async () => {
          try {
            setLoadingDismissAlert(true);
            await dismissAlert(params.row.id, userId || "");
            queryClient.removeQueries({ queryKey: ["exportAlertsData"] });
            setEnableSearchQuery(true);
          } catch (error) {
            console.error("Error dismissing alert:", error);
          } finally {
            setLoadingDismissAlert(false);
          }
        };

        handleDismissAlert();
      }
    }
  };

  useEffect(() => {
    setAlertsRequestData((prev: any) => ({
      ...prev,
      pageNumber: paginationModel?.page,
      pageSize: paginationModel?.pageSize,
      sortOrder: sortModel[0].sort,
      sortField: sortModel[0].field,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel]);

  // console.log("exportAlertsData", exportAlertsData);
  // console.log("filteredData", filteredData);

  return (
    <Paper style={{ padding: "16px", overflow: "hidden" }}>
      <ExportAlerts
        exportAlertsData={exportAlertsData}
        isLoadingAlerts={isLoadingAlerts}
        isSuccessAlerts={isSuccessAlerts}
        setAlertsRequestData={setAlertsRequestData}
        alertsFilteredRows={alertsFilteredRows}
      />
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <Box sx={{ minWidth: 800 }}>
          {exportAlertsData?.active_alerts?.length === 0 ? (
            <Typography variant="body1" align="center" sx={{ padding: "20px" }}>
              No alerts available.
            </Typography>
          ) : (
            <DataGrid
              loading={isLoadingAlerts || loadingDismissAlert}
              disableColumnMenu
              sx={{
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                },
                "& .MuiDataGrid-columnHeaders": {
                  borderBottom: "1px solid rgba(224, 224, 224, 1)",
                },
                "& .MuiDataGrid-virtualScroller": {
                  overflowX: "auto",
                },
              }}
              rows={alertsFilteredRows ?? []}
              columns={columns}
              paginationMode="server"
              rowCount={Math.ceil(totalRows)}
              onPaginationModelChange={(model: any) => {
                if (!isInitialRender) setPaginationModel(model);
              }}
              paginationModel={paginationModel}
              pageSizeOptions={[5, 10, 15, 50, 100]}
              getRowId={(row) => row.id} // Specify alert_id as the unique identifier for each row
              initialState={{
                sorting: {
                  sortModel: sortModel,
                },
              }}
              disableRowSelectionOnClick
              slotProps={{
                toolbar: {
                  showQuickFilter: true,
                },
              }}
              onCellClick={handleCellClick}
              onSortModelChange={handleSortModelChange}
              disableColumnFilter
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}
